import { useEffect, useState } from "react";
import { formatDropTime } from "../../../shared/i18n/index.ts";
import { api } from "../../lib/api.ts";
import { useI18n } from "../../lib/i18n.tsx";

type Table = Record<string, number | null>;
type Payload = {
  windows: Record<"d7" | "d30" | "d90", Table>;
  drops: { drop: number; listings: number; sold: number; buyers: number; purchases: number }[];
  computed_at: number;
};

const rows: [keyof Table & string, string, string][] = [
  ["listings_published", "Listings published", ""],
  ["listings_per_drop", "Listings live per Drop", "≥ 40"],
  ["sell_through_pct", "Sell-through %", "≥ 55"],
  ["median_time_to_first_sale_h", "Median time to first sale (h)", "≤ 36"],
  ["median_clearing_ratio_pct", "Median clearing / start %", "55–80"],
  ["buyer_no_show_pct", "Buyer no-show %", "≤ 8"],
  ["contact_share_within_24h_pct", "Contact shared within 24 h %", "≥ 90"],
  ["weekly_active_buyers", "Active buyers", "≥ 300 / week"],
  ["repeat_sellers_pct", "Repeat sellers %", "≥ 30"],
  ["purchases", "Purchases", ""],
  ["completed", "Completed", ""],
];

/** /admin/metrics — the §Success table for 7/30/90 days and per Drop (R-ADM-6). Plain tables. */
export function AdminMetrics() {
  const { locale } = useI18n();
  const [d, setD] = useState<Payload | null>(null);
  useEffect(() => {
    let live = true;
    api<Payload>("/api/admin/metrics")
      .then((p) => live && setD(p))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  if (!d) return <p className="muted">…</p>;
  return (
    <div className="stack">
      <div className="tablewrap">
        <table className="table" data-testid="metrics-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>7 d</th>
              <th>30 d</th>
              <th>90 d</th>
              <th>Target</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([k, label, target]) => (
              <tr key={k}>
                <td>{label}</td>
                {(["d7", "d30", "d90"] as const).map((w) => (
                  <td key={w}>{d.windows[w][k] ?? "—"}</td>
                ))}
                <td className="muted">{target}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3>Per Drop</h3>
      <div className="tablewrap">
        <table className="table">
          <thead>
            <tr>
              <th>Drop</th>
              <th>Listings</th>
              <th>Sold ≥ 1</th>
              <th>Purchases</th>
              <th>Buyers</th>
            </tr>
          </thead>
          <tbody>
            {d.drops.map((r) => (
              <tr key={r.drop}>
                <td>{formatDropTime(r.drop, locale, 0)}</td>
                <td>{r.listings}</td>
                <td>{r.sold}</td>
                <td>{r.purchases}</td>
                <td>{r.buyers}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
